/**
 * `SCR-ORDER` (19 states) and `SCR-ORDER-ASK` (5). FLOWS_SPEC §8.
 * OWNER: web-frontend-engineer.
 *
 * THE ASK ACTION IS RENDERED FROM `askAllowed`, WHICH THE SERVER DERIVES FROM
 * STATE (D-030). The front end holds no copy of the reversibility window,
 * exactly as it holds no copy of the transition table. It is re-evaluated from
 * real state on every render, so it cannot go stale.
 *
 * `EDGE-ASKWINDOW`: he opens the ask form in `placed`, the tailor confirms
 * before he submits, the server refuses — and HIS TYPED TEXT IS PRESERVED and
 * offered to the contact route. He wrote it in good faith and must not have to
 * write it twice.
 */

import { useEffect, useState } from 'react';
import { t } from '@pt/copy';
import { api } from '../lib/api.ts';
import { useResource, useSubmission } from '../lib/resource.ts';
import { dateInWords } from '@pt/view';
import { browserStore, holdAskDraft, readAskDraft, releaseAskDraft } from '../lib/askDraft.ts';
import { AppShell } from '../app/AppShell.tsx';
import { useNavigation } from '../app/Router.tsx';
import { useHouse } from '../app/SessionProvider.tsx';
import { StatusTimeline } from '../components/order.tsx';
import { Button, FactLine, Statement, TextInput } from '../components/primitives.tsx';
import { FetchError, LoadingRegion, OfflineStatement } from '../components/states.tsx';

export function OrderScreen({ orderRef }: { orderRef: string }) {
  const { navigate } = useNavigation();
  const { remember, contact } = useHouse();
  const order = useResource(() => api.order(orderRef), [orderRef]);
  const data = order.data;

  useEffect(() => {
    // The order names the tailor who holds the record. `EL-HUMAN` is a person
    // with a role, and this page is one of the two that knows which person.
    if (data !== null) {
      remember({ tailor: { displayName: data.measurementAuthorName, role: 'master_tailor' } });
    }
  }, [data, remember]);

  // EDGE-ASKWINDOW: text he typed before the window closed is offered back to
  // him here rather than discarded.
  const preserved = readAskDraft(browserStore(), orderRef);

  return (
    <AppShell
      title={t('order.heading', { orderRef })}
      width="ledger"
      banner={order.phase === 'offline' ? <OfflineStatement /> : null}
    >
      {order.phase === 'skeleton' || order.phase === 'idle' ? (
        <LoadingRegion rows={8} variant="ledger" slow={order.slow} label={orderRef} />
      ) : null}
      {order.phase === 'error' ? <FetchError whatKey="order.error.what" onRetry={order.reload} /> : null}

      {data === null ? null : (
        <>
          <StatusTimeline order={data} />

          <section className="pt-section">
            {data.askAllowed ? (
              // ST-ORDER-ASKABLE / -ASKABLE-HALTED. Two ordinary actions at
              // EQUAL WEIGHT: cancelling is exactly as easy as asking for a
              // change. No days, no workshop event, nothing about money.
              <div className="pt-actions pt-actions--equal">
                <Button onClick={() => navigate(`/orders/${orderRef}/ask?kind=change`)}>
                  {t('order.askable.change')}
                </Button>
                <Button onClick={() => navigate(`/orders/${orderRef}/ask?kind=cancel`)}>
                  {t('order.askable.cancel')}
                </Button>
              </div>
            ) : (
              // ST-ORDER-NOTASKABLE — the actions are ABSENT, not disabled, and
              // replaced by the contact route. It makes no claim about what can
              // or cannot still be done: that is Paul's to answer, not ours.
              <>
                <Statement>
                  {t('order.notaskable.statement', { contactName: contact?.displayName ?? data.measurementAuthorName })}
                </Statement>
                {preserved === '' ? null : (
                  <>
                    <FactLine>{t('ask.error.means')}</FactLine>
                    <p className="pt-record__notes">{preserved}</p>
                  </>
                )}
              </>
            )}
          </section>
        </>
      )}
    </AppShell>
  );
}

/**
 * `SCR-ORDER-ASK`. A RECORDED REQUEST routed to the named tailor, not a state
 * transition: THE CLIENT CANNOT CANCEL; HE ASKS, AND A HUMAN CANCELS.
 * The copy says exactly that and nothing more, and says nothing about money.
 */
export function OrderAskScreen({ orderRef, kind }: { orderRef: string; kind: 'change' | 'cancel' }) {
  const { navigate } = useNavigation();
  const { tailor } = useHouse();
  const submission = useSubmission();
  const [message, setMessage] = useState(() => readAskDraft(browserStore(), orderRef));
  const [recordedAt, setRecordedAt] = useState<string | null>(null);
  const [windowClosed, setWindowClosed] = useState(false);

  // His text is held from the first keystroke, so a refusal cannot lose it.
  useEffect(() => {
    holdAskDraft(browserStore(), orderRef, message);
  }, [message, orderRef]);

  return (
    <AppShell
      title={kind === 'cancel' ? t('ask.cancel.heading') : t('ask.change.heading')}
      width="ledger"
    >
      <Statement>
        {kind === 'cancel'
          ? t('ask.cancel.statement', { tailorName: tailor?.displayName ?? '' })
          : t('ask.change.statement', { tailorName: tailor?.displayName ?? '' })}
      </Statement>

      {recordedAt !== null ? (
        <Statement>
          {t('ask.recorded.statement', {
            recordedDate: dateInWords(recordedAt),
            tailorName: tailor?.displayName ?? '',
          })}
        </Statement>
      ) : windowClosed ? (
        // EDGE-ASKWINDOW — refused server-side while he was typing. The page
        // states the new position and HIS TEXT IS STILL HERE.
        <>
          <Statement announce>
            {t('order.notaskable.statement', { contactName: tailor?.displayName ?? '' })}
          </Statement>
          <FactLine>{t('ask.error.means')}</FactLine>
          <p className="pt-record__notes">{message}</p>
          <div className="pt-actions">
            <Button onClick={() => navigate(`/orders/${orderRef}`)}>{t('global.action.back')}</Button>
          </div>
        </>
      ) : (
        <>
          {submission.rejection === null ? null : <FetchError whatKey="ask.error.what" />}
          <TextInput label={t('ask.field.label')} value={message} onChange={setMessage} multiline />
          <div className="pt-actions">
            <Button
              variant="primary"
              busy={submission.busy}
              busyLabel={t('ask.busy.action')}
              onClick={() => {
                void submission
                  .run(() => api.askAboutOrder(orderRef, { kind, message }))
                  .then((result) => {
                    if (result !== null) {
                      setRecordedAt(result.recordedAt);
                      releaseAskDraft(browserStore(), orderRef);
                      return;
                    }
                    if (submission.rejection !== null) setWindowClosed(true);
                  });
              }}
            >
              {t('ask.action')}
            </Button>
          </div>
        </>
      )}
    </AppShell>
  );
}
